import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi';
import { useNavigate, Link } from 'react-router-dom';

const TermsAndConditions = () => {
    // Refs for each section
    const sectionRefs = {
        introduction: useRef(null),
        termsOfUse: useRef(null),
        userSubmissions: useRef(null),
        websiteInfo: useRef(null),
        accessibility: useRef(null),
        thirdPartyLinks: useRef(null),
        intellectualProperty: useRef(null),
        dataProtection: useRef(null),
        advertising: useRef(null),
        indemnity: useRef(null),
        lawJurisdiction: useRef(null),
        arbitration: useRef(null),
        termination: useRef(null),
        severability: useRef(null),
        miscellaneous: useRef(null),
        marketplace: useRef(null),
        delivery: useRef(null),
        generalPolicy: useRef(null)
    };

    // State for active tab and mobile menu
    const [activeTab, setActiveTab] = useState('introduction');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);

    const navigate = useNavigate();


    // Update scroll progress
    useEffect(() => {
        const handleScroll = () => {
            const mainContent = document.querySelector('.main-content');
            if (mainContent) {
                const scrollTop = mainContent.scrollTop;
                const scrollHeight = mainContent.scrollHeight - mainContent.clientHeight;
                const progress = (scrollTop / scrollHeight) * 100;
                setScrollProgress(progress);
            }
        };

        const mainContent = document.querySelector('.main-content');
        if (mainContent) {
            mainContent.addEventListener('scroll', handleScroll);
            return () => mainContent.removeEventListener('scroll', handleScroll);
        }
    }, []);

    // Scroll to section and update active tab
    const scrollToSection = (ref, sectionId) => {
        ref.current.scrollIntoView({ behavior: 'smooth' });
        setActiveTab(sectionId);
        setIsMobileMenuOpen(false);
    };

    // Sidebar item animation variants
    const sidebarItemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: (i) => ({
            opacity: 1,
            x: 0,
            transition: {
                delay: i * 0.05,
                duration: 0.3
            }
        })
    };

    // Content section animation variants
    const contentVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.4,
                ease: "easeOut"
            }
        }
    };

    // Mobile menu toggle
    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    return (
        <div className="flex flex-col md:flex-row h-screen bg-customDark">
            {/* Mobile Menu Button */}
            <div className="md:hidden fixed bottom-4 right-4 z-50">
                <button
                    onClick={toggleMobileMenu}
                    className="p-2 rounded-md bg-customGold text-white"
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 6h16M4 12h16M4 18h16"
                        />
                    </svg>
                </button>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-70 z-40 md:hidden"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />
                )}
            </AnimatePresence>

            {/* Sidebar Navigation - Mobile */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ type: 'tween', ease: 'easeInOut' }}
                        className="fixed inset-y-0 left-0 w-64 bg-customGray z-40 overflow-y-auto md:hidden"
                    >
                        {/* Header with Back Button */}
                        <div className="p-4 flex items-center space-x-3">
                            <button
                                onClick={() => navigate(-1)}
                                className="text-white hover:text-customGold"
                            >
                                <HiOutlineChevronLeft size={24} />
                            </button>

                            <h2 className="text-xl font-bold text-white">Terms & Conditions</h2>
                        </div>

                        {/* Navigation Items */}
                        <nav className="space-y-1 px-4">
                            {Object.entries({
                                introduction: 'Introduction',
                                termsOfUse: 'Terms of Use',
                                userSubmissions: 'User Submissions',
                                websiteInfo: 'Website Information',
                                accessibility: 'Accessibility',
                                thirdPartyLinks: 'Third-Party Links',
                                intellectualProperty: 'Intellectual Property',
                                dataProtection: 'Data Protection',
                                advertising: 'Advertising & Cookies',
                                indemnity: 'Indemnity',
                                lawJurisdiction: 'Law & Jurisdiction',
                                arbitration: 'Arbitration',
                                termination: 'Termination',
                                severability: 'Severability',
                                miscellaneous: 'Miscellaneous',
                                marketplace: 'Marketplace',
                                delivery: 'Delivery',
                                generalPolicy: 'General Policy'
                            }).map(([id, title], index) => (
                                <motion.button
                                    key={id}
                                    custom={index}
                                    initial="hidden"
                                    animate="visible"
                                    variants={sidebarItemVariants}
                                    onClick={() => scrollToSection(sectionRefs[id], id)}
                                    className={`w-full text-left px-4 py-3 rounded-md flex items-center ${activeTab === id
                                        ? 'bg-customGold text-white font-medium'
                                        : 'text-white hover:bg-gray-600'
                                        }`}
                                >
                                    <HiOutlineChevronRight className="mr-2" />
                                    {title}
                                </motion.button>
                            ))}
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>


            {/* Sidebar Navigation - Desktop */}
            <div className="hidden md:block w-64 bg-customGray overflow-y-auto sticky top-0 h-screen">
                <div className="p-4">
                    <div className="flex items-center gap-3 mb-6">
                        {/* Back arrow */}
                        <button
                            onClick={() => navigate(-1)}
                            className="text-white hover:text-customGold transition"
                            aria-label="Go back"
                        >
                            <HiOutlineChevronLeft size={24} />
                        </button>

                        {/* Title */}
                        <motion.h2
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-xl font-bold text-white"
                        >
                            Terms & Conditions
                        </motion.h2>
                    </div>

                    <nav className="space-y-1">
                        {Object.entries({
                            introduction: 'Introduction',
                            termsOfUse: 'Terms of Use',
                            userSubmissions: 'User Submissions',
                            websiteInfo: 'Website Information',
                            accessibility: 'Accessibility',
                            thirdPartyLinks: 'Third-Party Links',
                            intellectualProperty: 'Intellectual Property',
                            dataProtection: 'Data Protection',
                            advertising: 'Advertising & Cookies',
                            indemnity: 'Indemnity',
                            lawJurisdiction: 'Law & Jurisdiction',
                            arbitration: 'Arbitration',
                            termination: 'Termination',
                            severability: 'Severability',
                            miscellaneous: 'Miscellaneous',
                            marketplace: 'Marketplace',
                            delivery: 'Delivery',
                            generalPolicy: 'General Policy'
                        }).map(([id, title], index) => (
                            <motion.button
                                key={id}
                                custom={index}
                                initial="hidden"
                                animate="visible"
                                variants={sidebarItemVariants}
                                onClick={() => scrollToSection(sectionRefs[id], id)}
                                className={`w-full text-left px-4 py-2 rounded-md flex items-center ${activeTab === id ? 'bg-customGold text-white font-medium' : 'text-white hover:bg-gray-600'}`}
                            >
                                <HiOutlineChevronRight className="mr-2" />
                                {title}
                            </motion.button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto p-8 main-content relative">
                {/* Scroll Progress Bar */}
                <motion.div
                    className="fixed top-0 left-0 right-0 h-1 bg-customGold z-30"
                    style={{ width: `${scrollProgress}%` }}
                    initial={{ width: 0 }}
                />

                {/* Introduction Section */}
                <motion.section
                    ref={sectionRefs.introduction}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12"
                >
                    <h2 className="text-2xl font-bold border-b border-gray-200 pb-2 text-white">INTRODUCTION</h2>
                    <div className="space-y-4 text-white">
                        <p>
                            Welcome to the blackdefynition.com website (the "Site"). These terms & conditions ("Terms and Conditions") apply to the site, and all of its divisions, subsidiaries, and affiliate-operated Internet sites, which reference these Terms and Conditions.
                        </p>
                        <p>
                            This website is owned and operated by Blackdefynition. For this website, "we," "us," and "our" all refer to blackdefynition.com.
                        </p>
                        <p>
                            The site reserves the right to change, modify, add, or remove portions of the Terms and Conditions of Use and the Terms and Conditions of Sale at any time. Changes will be effective when posted on the site with no other notice provided. Please check these terms and conditions regularly for updates. Your continued use of the site following the posting of changes to these Terms and Conditions constitutes your acceptance of those changes.
                        </p>
                        <p>
                            Kindly review the Terms and Conditions listed below diligently before using this website, as your use of the website indicates your agreement to be wholly bound by its Terms and Conditions without modification.
                        </p>
                        <p>
                            You agree that if you are unsure of the meaning of any part of these Terms and Conditions or have any questions regarding the Terms and Conditions, you will not hesitate to contact us for clarification. These Terms and Conditions fully govern the use of this website—no extrinsic evidence, whether oral or written, will be incorporated.
                        </p>
                    </div>
                </motion.section>

                {/* Terms of Use Section */}
                <motion.section
                    ref={sectionRefs.termsOfUse}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12"
                >
                    <h2 className="text-2xl font-bold border-b border-gray-200 pb-2 text-white">TERMS AND CONDITIONS OF USE</h2>
                    <div className="space-y-4 text-white">
                        <h3 className="text-xl font-semibold text-customGold">USE OF THE SITE</h3>
                        <p>
                            You confirm that you are at least 18 years of age or are accessing the site under the supervision of a parent or legal guardian.
                        </p>
                        <p>
                            Both parties agree that this website may only be used following these Terms and Conditions of Use. If you do not agree with the Terms and Conditions of Use or do not wish to be bound by them, you agree to refrain from using this website.
                        </p>
                        <p>
                            We grant you a non-transferable, revocable, and non-exclusive license to use this site, following the Terms and Conditions of Use, for such things as shopping for personal items sold on the site, gathering preliminary information regarding our products and services, and making purchases. Commercial use or use on behalf of any third party is prohibited except as explicitly permitted by us in advance.
                        </p>
                        <p>
                            These Terms and Conditions of Use specifically prohibit actions such as accessing our servers or internal computer systems, interfering in any way with the functionality of this website, gathering or altering any underlying software code, and infringing any intellectual property rights. This list is non-exhaustive and similar actions are also strictly prohibited.
                        </p>
                        <p>
                            Any breach of these Terms and Conditions of Use shall result in the immediate revocation of the license granted in this paragraph without prior notice to you. Should we determine at our sole discretion that you are in breach of these conditions, we reserve the right to deny you access to this website and its contents and do so without prejudice to any available remedies at law or otherwise.
                        </p>
                        <p>
                            Certain services and related features that may be made available on the site may require registration or subscription. Should you register or subscribe for any such services or related features, you agree to provide accurate and current information about yourself and promptly update such information if there are any changes. Every site user is solely responsible for keeping passwords and other account identifiers safe and secure.
                        </p>
                        <p>
                            The account owner is responsible for all activities under such a password or account. Furthermore, you must notify us of the unauthorized use of your password or account. The site shall not be responsible or liable, directly or indirectly, in any way for any loss or damage incurred as a result of, or in connection with, your failure to comply with this section.
                        </p>
                        <p>
                            During the registration process, you agree to receive promotional emails from the site. You can subsequently opt out of receiving such promotional e-mails by clicking on the link at the bottom of any promotional email.
                        </p>
                    </div>
                </motion.section>

                {/* User Submissions Section */}
                <motion.section
                    ref={sectionRefs.userSubmissions}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">User Submissions</h3>
                    <p>
                        Anything you submit to the site and provide to us, including but not limited to questions, reviews, activity, media, comments, and suggestions (collectively, <strong>"Submissions"</strong>), will become our sole and exclusive property and shall not be returned to you.
                    </p>
                    <p>
                        In addition to the rights applicable to any Submission, when you post comments or reviews to the site, you also grant us the right to use the name you submit in connection with such review, comment, or other content.
                    </p>
                    <p>
                        You shall not use a false e-mail address, pretend to be someone other than yourself, or otherwise mislead third parties about the origin of any Submissions or us. We may, but shall not be obligated to, remove or edit any Submissions.
                    </p>
                    <p>
                        By completing an order or signing up, you agree to receive:
                    </p>
                    <ul className="list-decimal list-inside pl-4 space-y-1">
                        <li>Emails associated with finalizing your order, which may contain relevant offers from third parties.</li>
                        <li>Emails asking you to review Blackdefynition and your purchase.</li>
                        <li>Promotional emails, SMS, and push notifications from Blackdefynition.</li>
                    </ul>
                    <p>
                        You may unsubscribe from promotional emails via a link provided in each email. If you want us to remove your personal information from our database and unsubscribe from emails and SMS, please email the Customer Service email address by country.
                    </p>
                </motion.section>

                {/* Website Information Section */}
                <motion.section
                    ref={sectionRefs.websiteInfo}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">
                        Information Available on the Website
                    </h3>
                    <p>
                        You accept that the information contained in this website is provided
                        <strong> "as is, where is" </strong>, is intended for information purposes only
                        and is subject to change without notice. Although we take reasonable steps to
                        ensure the accuracy of the information, and we believe the information to be
                        reliable when posted, it should not be relied upon, and it does not in any way
                        constitute either a representation, a warranty, or a guarantee.
                    </p>
                    <p>
                        Product representations expressed on this site are those of the vendor and are
                        not made by us. Submissions or opinions expressed on this site are those of the
                        individual posting such content and may not reflect our opinions.
                    </p>
                </motion.section>

                {/* Accessibility Section */}
                <motion.section
                    ref={sectionRefs.accessibility}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">
                        Accessibility of Website
                    </h3>
                    <p>
                        We aim to ensure accessibility to the website at all times. However, we do not represent that nature and reserve the right to terminate the website at any time and without notice. You accept that service interruption may occur to allow for website improvements and scheduled maintenance or may also be due to outside factors beyond our control.
                    </p>
                </motion.section>

                {/* Third-Party Links Section */}
                <motion.section
                    ref={sectionRefs.thirdPartyLinks}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">
                        Links and Third-Party Websites
                    </h3>
                    <p>
                        We may include links to third-party websites at any time. However, a link to another website should not be considered as an affiliation or a partnership with a third party or viewed as an endorsement of a particular website unless explicitly stated otherwise.
                    </p>
                    <p>
                        If the user follows a link to another website, they do so at their own risk. We accept no responsibility for any content, including, but not limited to, information, products, and services available on third-party websites.
                    </p>
                    <p>
                        Creating a link to this website is strictly forbidden without our prior written consent. Furthermore, we reserve the right to revoke our consent without notice or justification.
                    </p>
                </motion.section>

                {/* Intellectual Property Section */}
                <motion.section
                    ref={sectionRefs.intellectualProperty}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">
                        Intellectual Property
                    </h3>
                    <p>
                        Both parties agree that all intellectual property rights and database rights, whether registered or unregistered, in the site, the information contained on the site, and all the website design, including, but not limited to, text, graphics, software, photos, video, music, sound, and their selection and arrangement, and all software compilations, underlying source code, and software shall remain at all times vested in us or our licensors. Such material will only be permitted as expressly authorized by us or our licensors.
                    </p>
                    <p>
                        Any unauthorized use of the material and content of this website is strictly prohibited, and you agree not to or facilitate any third party to copy, reproduce, transmit, publish, display, distribute, commercially exploit or create derivative works of such material and content.
                    </p>
                </motion.section>

                {/* Data Protection Section */}
                <motion.section
                    ref={sectionRefs.dataProtection}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">
                        Data Protection
                    </h3>
                    <p>
                        Any personal information collected using this website will be held and used in accordance with our{" "}
                        <Link to="/privacy-policy" className="text-customGold hover:underline">
                            Privacy Policy
                        </Link>
                        , and the Advertising & Cookies section below, which together set out how your data and cookies are used, including for advertising purposes.
                    </p>
                </motion.section>

                {/* Advertising & Cookies Section */}
                <motion.section
                    ref={sectionRefs.advertising}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">
                        Advertising & Cookies
                    </h3>
                    <p>
                        This section is a summary. For the full details on the categories of cookies we use, how to manage your preferences, and your data protection rights, please see our{" "}
                        <Link to="/privacy-policy" className="text-customGold hover:underline">
                            Privacy Policy
                        </Link>
                        . You can accept, reject, or customize non-essential cookies at any time via the "Cookie Settings" link in the site footer.
                    </p>
                    <p>
                        We use third-party advertising companies, including Google, to serve ads when you visit this site. These companies, including Google, use cookies and similar technologies (such as the DART cookie) to serve ads based on your prior visits to this website and other websites on the Internet.
                    </p>
                    <p>
                        Google's use of advertising cookies enables it and its partners to serve ads to you based on your visit to this site and/or other sites on the Internet. This may include the use of Google AdSense and other third-party ad networks or vendors, which may also use cookies to serve ads on this site.
                    </p>
                    <p>
                        You may opt out of personalized advertising by visiting{" "}
                        <a
                            href="https://adssettings.google.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-customGold hover:underline"
                        >
                            Google Ads Settings
                        </a>
                        . Alternatively, you can opt out of a third-party vendor's use of cookies for personalized advertising by visiting{" "}
                        <a
                            href="https://www.aboutads.info/choices/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-customGold hover:underline"
                        >
                            www.aboutads.info
                        </a>
                        .
                    </p>
                    <p>
                        Cookies are small text files stored on your device that allow us and our advertising partners to recognize your browser and to collect and use data about you and your use of the site for analytics, personalization, and to serve advertising that is relevant to your interests. You can control or disable cookies through your browser settings; however, doing so may affect the functionality of this website and the ads you see.
                    </p>
                    <p>
                        For more information on how Google uses data when you use our site or the sites and apps of our partners, please visit{" "}
                        <a
                            href="https://policies.google.com/technologies/partner-sites"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-customGold hover:underline"
                        >
                            How Google uses information from sites or apps that use our services
                        </a>
                        .
                    </p>
                </motion.section>

                {/* Indemnity Section */}
                <motion.section
                    ref={sectionRefs.indemnity}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">
                        Indemnity
                    </h3>
                    <p>
                        You agree to indemnify and hold us, our affiliates, officers, directors, agents, and employees, as the case may be, free from any claim or demand, including reasonable legal fees, related to your breach of these Terms of Use and User Agreement.
                    </p>
                </motion.section>

                {/* Law & Jurisdiction Section */}
                <motion.section
                    ref={sectionRefs.lawJurisdiction}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">
                        Applicable Law and Jurisdiction
                    </h3>
                    <p>
                        These Terms and Conditions of Use shall be interpreted and governed by the laws in force in the Federal Republic of Nigeria and across Africa. Subject to the Arbitration section below, each party now agrees to submit to the jurisdiction of Nigeria's courts and waive any objections based upon venue.
                    </p>
                </motion.section>

                {/* Arbitration Section */}
                <motion.section
                    ref={sectionRefs.arbitration}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">
                        Arbitration
                    </h3>
                    <p>
                        Any controversy, claim, or dispute arising out of or relating to these Terms and Conditions of Use will be referred to and finally settled by private and confidential binding arbitration before a single arbitrator held in Nigeria and governed by Nigeria law under the Arbitration and Conciliation Act Cap A18 Laws of the Federation of Nigeria 2004, as amended, replaced or re-enacted from time to time.
                    </p>
                    <p>
                        The arbitrator shall be a legally trained person who has experience in the information technology field in Nigeria and is independent of either party. Notwithstanding the preceding, the site reserves the right to pursue the protection of intellectual property rights and confidential information through injunctive or other equitable relief through the courts.
                    </p>
                </motion.section>

                {/* Termination Section */}
                <motion.section
                    ref={sectionRefs.termination}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">
                        Termination
                    </h3>
                    <p>
                        In addition to any other legal or equitable remedies, we may immediately terminate the Terms and Conditions of Use without prior notice to you or revoke any or all of your rights granted under the Terms and Conditions of Use.
                    </p>
                    <p>
                        Upon any termination of this agreement, you shall immediately cease all access to and use of the site, and we shall, in addition to any other legal or equitable remedies, immediately revoke all password(s) and account identification issued to you and deny your access to and use of this site in whole or in part.
                    </p>
                    <p>
                        Any termination of this agreement shall not affect the respective rights and obligations (including, without limitation, payment obligations) of the parties arising before the termination date. You agree that the site shall not be liable to you or any other person due to such suspension or termination.
                    </p>
                    <p>
                        If you are dissatisfied with the site or any terms, conditions, rules, policies, guidelines, or practices of E-Cart Internet Services Nigeria Limited in operating the site, your sole and exclusive remedy is to discontinue using the site.
                    </p>
                </motion.section>

                {/* Severability Section */}
                <motion.section
                    ref={sectionRefs.severability}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">Severability</h3>
                    <p>
                        If any court or tribunal holds any portion of these terms or conditions to be invalid or unenforceable, either in whole or in part, then that part shall be severed from these Terms and Conditions of Use and shall not affect the validity or enforceability of any other section listed in this document.
                    </p>
                </motion.section>

                {/* Miscellaneous Section */}
                <motion.section
                    ref={sectionRefs.miscellaneous}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">Miscellaneous Provisions</h3>
                    <p>
                        You agree that all agreements, notices, disclosures, and other communications we provide to you electronically satisfy any legal requirement that such communications be in writing.
                    </p>
                    <p>
                        Assigning or sub-contracting any of your rights or obligations under these Terms and Conditions of Use to any third party is prohibited unless agreed upon in writing by the seller.
                    </p>
                    <p>
                        We reserve the right to transfer, assign, or sub-contract the benefit of the whole or part of any rights or obligations under these Terms and Conditions of Use to any third party.
                    </p>
                </motion.section>

                {/* Marketplace Section */}
                <motion.section
                    ref={sectionRefs.marketplace}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h3 className="text-xl font-semibold text-customGold uppercase">Terms and Conditions of Sale</h3>

                    <h4 className="text-lg font-semibold text-customGold">General</h4>
                    <p>
                        You confirm that you are at least 18 years of age or are accessing the site under the supervision of a parent or legal guardian. You agree that if you are unsure of the meaning of any part of the Terms and Conditions of Sale, you will not hesitate to contact us for clarification before making a purchase.
                    </p>
                    <p>
                        These Terms and Conditions of Sale fully govern the sale of goods and services purchased on this site—no extrinsic evidence, whether oral or written, will be incorporated.
                    </p>

                    <h4 className="text-lg font-semibold text-customGold">Formation of Contract</h4>
                    <p>
                        Both parties agree that browsing the website and gathering information regarding the seller's services does not constitute an offer to sell but merely an invitation to treat. The parties accept that a request is only made once you have selected the item you intend to purchase, chosen your preferred payment method, proceeded to the checkout, and completed the checkout process.
                    </p>
                    <p>
                        Both parties agree that the offer is not accepted when the seller contacts you by phone or email to confirm that the order has been placed online. Your request is only received when the seller dispatches the product to you and informs you by email or phone of the dispatch of your ordered product.
                    </p>
                    <p>
                        Before your order is confirmed, you may be asked to provide additional verifications or information, including but not limited to phone number and address, before the seller accepts the order.
                    </p>
                    <p>
                        Please note that there are cases when an order cannot be processed for various reasons. The seller reserves the right to refuse or cancel any order for any reason at any given time.
                    </p>

                    <h4 className="text-lg font-semibold text-customGold">Acceptance of Electronic Documents</h4>
                    <p>
                        You agree that all agreements, notices, disclosures, and other communications we provide to you electronically satisfy any legal requirement that such communications be in writing.
                    </p>

                    <h4 className="text-lg font-semibold text-customGold">Liability of Parties on the Blackdefynition Marketplace</h4>
                    <p>
                        We also operate a marketplace for third parties to sell their products on our website. None of the products listed on the Blackdefynition Marketplace are owned or sold by us. Blackdefynition stands as an intermediary between the buyer and the seller. Buyers can initiate disputes when goods are delivered in poor condition, when due, or as seen in the marketplace. The seller agrees to be held responsible for any liability in contract, pre-contract, or other representations in tort for all transactions conducted on the Blackdefynition Marketplace.
                    </p>
                </motion.section>

                {/* Delivery Section */}
                <motion.section
                    ref={sectionRefs.delivery}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h2 className="text-xl font-semibold text-customGold">DELIVERY</h2>
                    <p>
                        This site is only for delivering products to customers within Africa (list the African countries you can cover for delivery), the United States of America, and the United Kingdom.
                        We make every effort to deliver goods within the estimated timescales set out on our site; however, delays are occasionally inevitable due to unforeseen factors.
                        We shall be under no liability for any delay or failure to deliver the products within the estimated timescales where they did not occur due to our fault or negligence.
                    </p>
                    <p>
                        You agree not to hold the seller liable for any delay or failure to deliver products or otherwise perform any obligation as specified in these Terms and Conditions of Sale if the same is wholly or partly caused, directly or indirectly, by circumstances beyond our reasonable control.
                    </p>
                </motion.section>

                {/* General Policy Section */}
                <motion.section
                    ref={sectionRefs.generalPolicy}
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="mb-12 space-y-4 text-white"
                >
                    <h2 className="text-xl font-semibold text-customGold">GENERAL POLICY FOR PAGE, GROUP, AND EVENTS</h2>
                    <p>
                        The following terms, our Data Policy, Community Standards, and Terms of service, apply to all creators and administrators of Pages, Groups, and Events on Blackdefynition. You are responsible for ensuring that your Page, Group, or Event complies with all applicable laws, statutes, and regulations.
                    </p>

                    <h3 className="text-lg font-semibold text-customGold">Prohibited Business Practices</h3>
                    <p>
                        Pages, groups, and events must not promote products, services, schemes, or offers using deceptive or misleading business practices, including those intended to scam people out of money or personal information.
                    </p>

                    <h3 className="text-lg font-semibold text-customGold">Impersonation</h3>
                    <p>
                        Pages, groups, and events must not impersonate or falsely represent a brand, entity, or public figure. Where a Page, Group, or Event is being used to express support for or interest in a brand, entity, or public figure, it must make clear in the name or description that it is not an official representation.
                    </p>

                    <h3 className="text-lg font-semibold text-customGold">Gambling</h3>
                    <p>
                        Pages, groups, and events must not promote online gambling or gaming where anything of monetary value (including digital currencies) is required to play. Anything of monetary value forms part of the prize without our prior written permission. Common gambling or gaming activities that may require permission include betting, lotteries, raffles, casino, fantasy sports, bingo, poker, skill game tournaments, and sweepstakes.
                    </p>

                    <h3 className="text-lg font-semibold text-customGold">Inaccurately Tagged Content</h3>
                    <p>
                        Pages, Groups, and Events must not inaccurately tag content or encourage users to tag content inaccurately.
                    </p>

                    <h3 className="text-lg font-semibold text-customGold">Incentivization</h3>
                    <p>
                        Pages, Groups, and Events must not incentivize people to misuse Blackdefynition features or functionality.
                    </p>

                    <h3 className="text-lg font-semibold text-customGold">Misleading Treatment Centers</h3>
                    <p>
                        Pages, Groups, and Events must not promote drug and alcohol addiction treatment centers that are misleading, engaging in practices that are harmful to patients, or otherwise violating applicable laws.
                    </p>

                    <p>
                        All Platform Pages, Groups, or Events must comply with Blackdefynition's community standards.
                    </p>

                    <h3 className="text-lg font-semibold text-customGold">Promotions on Pages, Groups, and Events</h3>
                    <p>
                        If you use Blackdefynition to communicate or administer a promotion (e.g., a contest), you are responsible for the lawful operation of that promotion, including the official rules; offer terms and eligibility requirements (e.g., age and residency restrictions); and compliance with applicable laws and regulations governing the promotion and all prizes offered (e.g., registration and obtaining necessary regulatory approvals).
                    </p>
                    <p>
                        Promotions may be administered on Pages, Groups, and Events on Blackdefynition's web page. Personal Timelines and friend connections must not be used to administer promotions (e.g., "share on your Timeline to enter" or "share on your friend's Timeline to get additional entries" and "tag your friends in this post to enter" are not permitted).
                    </p>
                    <p>
                        Blackdefynition will not assist you in administering your promotion, and you agree that if you use our service to administer your promotion, you do so at your own risk.
                    </p>

                    <h3 className="text-lg font-semibold text-customGold">Collection of Data on Pages, Groups, and Events</h3>
                    <p>
                        Suppose you collect content and information directly from users. In that case, your Page, Group, or Event must make it clear that you (and not Blackdefynition) are collecting it and must provide notice about and obtain user consent for your use of the content and information you collect.
                    </p>
                    <p>
                        Regardless of how you obtain content and information from users, you are responsible for securing all necessary permissions to reuse their content and information.
                    </p>
                    <p>
                        Your Page, Group, or Event must not collect users' content or information or otherwise access Blackdefynition using automated means (such as harvesting bots, robots, spiders, or scrapers) without written permission from Blackdefynition.
                    </p>

                    <h3 className="text-lg font-semibold text-customGold">Pages-Specific Policies</h3>
                    <p>
                        Page names must not include profanity, excessively incorrect grammar or punctuation, and must not consist solely of generic terms or locations.
                    </p>
                    <p>
                        Content posted to a Page is public and can be viewed by everyone who can see the Page.
                    </p>
                    <p>
                        Your Page's cover photo or profile picture must not include a verified checkmark and must not have third-party products, brands, or sponsors.
                    </p>
                    <p>
                        Pages must not promote the sale of prescription pharmaceuticals. Pages for online pharmacies may be permitted with Blackdefynition's prior approval.
                    </p>
                    <p>
                        You must restrict access by age using Blackdefynition's Page functionality as necessary to comply with applicable laws and regulations.
                    </p>
                    <p>
                        When sharing a third-party link from your Page, you must not edit any elements of the post preview.
                    </p>
                    <p>
                        You must not misuse Blackdefynition's upload functionality in connection with your Page.
                    </p>
                    <p>
                        Pages promoting regulated goods or services must comply with our Community Standards.
                    </p>
                    <p>
                        You must not accept anything of value to drive traffic to an app or site that engages in traffic or one that contains a disproportionate volume of ads relative to content, or content that is highly sexual, shocking, clickbait, or promotes scams.
                    </p>
                    <p>
                        Other admins of the Page may reshare the content you post to your Page to other Blackdefynition Products connected to the Page.
                    </p>
                    <p>
                        Your Page must include all necessary disclosures to people using Blackdefynition, such as any disclosures needed to indicate the commercial nature of the content posted by you.
                    </p>

                    <h3 className="text-lg font-semibold text-customGold">Events-Specific Policies</h3>
                    <p>
                        Events cannot use automated means to generate event invites.
                    </p>
                    <p>
                        Events must provide clear and up-front disclosure for any actions or additional costs associated with an event ticket purchase.
                    </p>
                    <p>
                        Changes to an event must not result in a misleading or unintended connection and must not substantially change the event.
                    </p>

                    <h3 className="text-lg font-semibold text-customGold">Clarifying Our Guidelines for Music in Video</h3>
                    <ul className="list-disc pl-6">
                        <li>Music in stories and traditional live music performances (e.g., filming an artist or band performing live) are permitted.</li>
                        <li>The greater the number of full-length recorded tracks in a video, the more likely it may be limited (more below what we mean by "limited").</li>
                        <li>Shorter clips of music are recommended.</li>
                        <li>Video must not be more than 30Mb for either Reels, Stories, or profile uploads.</li>
                        <li>You have a maximum of 1 gig stream storage, but you can buy more storage if you wish to save your live streams.</li>
                    </ul>
                    <p>
                        These guidelines are consistent across life and recorded video on both Blackdefynition and for all types of accounts on the platforms — i.e., pages, profiles, and verified and unverified accounts.
                    </p>
                    <p>
                        We know it can be frustrating to have a stream interrupted or to have parts of your video muted because it includes music. We're also improving our in-product notifications to minimize these interruptions and prevent confusion around audio use. These notifications are intended to alert you when our systems detect that your broadcast or uploaded video may include music in a way that doesn't adhere to our licensing agreements.
                    </p>
                    <p>
                        With these improvements, we look forward to people, artists, and creators continuing to use music to share and bond with each other across our platforms.
                    </p>
                    <p>
                        We will continue to work hand-in-hand with our music partners to enable people to build a community with music in new ways. And during this time, we're working to increase people's ability to express themselves through our products.
                    </p>
                </motion.section>

                {/* Contact Information */}
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={contentVariants}
                    className="bg-customGray border-l-4 border-customGold p-4 mt-12"
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                >
                    <p className="text-white">
                        If you have any questions about these Terms and Conditions, please contact us at <a href="mailto:legal@blackdefynition.com" className="hover:underline text-customGold">legal@blackdefynition.com</a>.
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default TermsAndConditions;